// features/orders/components/OrdersList.jsx
import React from 'react'
import { useOrderPrefetch } from '../hooks/useOrderPrefetch'
import { Badge } from '../../../components/ui/Badge'
import { SkeletonList } from '../../../components/ui/Skeleton'
import { EmptyState } from '../../../components/feedback/EmptyState'
import { formatMoney, formatDate } from '../../../lib/format'
import { cn } from '../../../lib/cn'
import { ShoppingCart, Users, CheckCircle, Clock, AlertCircle, XCircle } from 'lucide-react'

function getPaymentBadge(order) {
  const mode = order?.payment_mode || order?.payment?.mode || ''

  if (mode === 'cancelled') return { tone: 'red', text: 'Annulée', icon: XCircle }
  if (mode === 'paid' || mode === 'cash') return { tone: 'green', text: 'Payée', icon: CheckCircle }
  if (mode === 'credit') return { tone: 'yellow', text: 'Crédit', icon: AlertCircle }
  if (mode === 'installment') return { tone: 'blue', text: 'Échéances', icon: Clock }

  return { tone: 'neutral', text: mode || '—', icon: null }
}

export function OrdersList({
  orders = [],
  loading = false,
  selectedId,
  onSelect,
  selected = [],
  onToggleSelect,
}) {
  const { prefetch } = useOrderPrefetch()

  if (loading && orders.length === 0) {
    return <SkeletonList count={5} />
  }

  if (!loading && orders.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={<ShoppingCart className="h-8 w-8" />}
          title="Aucune vente"
          description="Aucune vente ne correspond aux filtres sélectionnés."
        />
      </div>
    )
  }

  return (
    <div className="divide-y divide-slate-100">
      {orders.map((order) => {
        const badge = getPaymentBadge(order)
        const BadgeIcon = badge.icon
        const isSelected = selectedId === order.id
        const isChecked = selected.includes(order.id)
        const isCancelled = order.payment_mode === 'cancelled'

        return (
          <div
            key={order.id}
            onClick={() => onSelect?.(order.id)}
            onMouseEnter={() => prefetch(order.id)}
            className={cn(
              'p-4 cursor-pointer transition-colors',
              'hover:bg-slate-50',
              isSelected && 'bg-purple-50 border-l-4 border-l-purple-600',
              isCancelled && 'opacity-60'
            )}
          >
            <div className="flex items-start gap-3">
              {/* Checkbox */}
              {onToggleSelect && (
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    e.stopPropagation()
                    onToggleSelect(order.id)
                  }}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                />
              )}

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-bold text-slate-900 truncate">
                    {order.external_id || order.id}
                  </span>
                  <Badge tone={badge.tone} icon={BadgeIcon}>
                    {badge.text}
                  </Badge>
                </div>

                <div className="flex items-center gap-2 mt-1 text-xs text-slate-600">
                  <Users className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">
                    {order.buyer?.name || order.buyer_name || 'Client inconnu'}
                  </span>
                  {(order.buyer?.phone || order.buyer_phone) && (
                    <>
                      <span className="text-slate-300">•</span>
                      <span>{order.buyer?.phone || order.buyer_phone}</span>
                    </>
                  )}
                </div>

                <div className="flex items-center justify-between mt-2">
                  <div className="text-lg font-bold text-slate-900">
                    {formatMoney(order.total_ttc || 0)}
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatDate(order.created_at, { hour: undefined, minute: undefined })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
